import { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import './App.css';

const stripePromise = loadStripe('pk_live_51RADxoKb4y4i6za2zfsd2f5RPbchgmeObQEdOxyJpQTIYZkXoWKvIlHwmlTisJRt5gCcVgOG4UMRcGN0SmJ32qf000OLWXKHJn');

const PRICE_ID = 'price_1The18Kb4y4i6za2yxsBT1hp';

const BEATS = [
  {
    id: 'beat1',
    emoji: '01',
    title: 'Acknowledge',
    tagline: 'Mirror back their original signal in their exact language.',
    deliverables: [
      'Trigger-specific human openers (Inbound, Comment, Story Reply)',
      'Seamless transition patterns from ManyChat to human setter',
      'Instant profile qualification in line one to drop bad fits',
    ],
    promise: 'The buyer leans in. Conversational trust established instantly.',
  },
  {
    id: 'beat2',
    emoji: '02',
    title: 'Diagnose',
    tagline: 'Ask one highly targeted question that surfaces explicit pain points.',
    deliverables: [
      'Six custom diagnostic vectors (Outcome, Timeline, Obstacle, Budget)',
      'Under-30-second text frames to confirm lead value fields',
      'Strict single-question guardrails to eliminate chat anxiety',
    ],
    promise: 'They shift to discussing operational bottlenecks, not the weather.',
  },
  {
    id: 'beat3',
    emoji: '03',
    title: 'Permission',
    tagline: 'Get them to explicitly ask for your framework offer.',
    deliverables: [
      'Soft-pivot transition paths scaled to thread temperature',
      'Value-framing strategies that present the price transparently',
      'Psychological exit options that counterintuitively raise reply rates by 20%',
    ],
    promise: 'They grant you explicit authorization to reveal the pricing loop.',
  },
  {
    id: 'beat4',
    emoji: '04',
    title: 'Pivot',
    tagline: 'Deliver the next step, hold your price ground, and close.',
    deliverables: [
      'High-converting 90-second voice note system scripts',
      'One-line friction reframes for timing, cost, and drop-off objections',
      'Frictionless call booking, payment link, or app intake handoffs',
    ],
    promise: 'They execute the intended conversion goal: book, pay, or apply.',
  },
];

const VALUE_STACK = [
  {
    module: '6 Target Conversion Paths',
    value: 'Tailored contextual logic for True Inbound, ManyChat Handoffs, Warm Followers, and Engagement Drops—with an integrated LinkedIn modifier to transition cleanly between platforms.',
  },
  {
    module: 'Path 5: The 3-Window Revival',
    value: 'Exact text re-engagement scripts for 48-hour, 10-day, and 30-day stale ghosting points.',
  },
  {
    module: 'DM Objection Matrix',
    value: 'Ultra-fast, single-line verbal reframes for "How much?", "Too expensive", or "Maybe later" queries.',
  },
  {
    module: 'Weekly Health Dashboard',
    value: 'Data targets to evaluate and rescue slipping reply rates, response velocity, and pivot ratios weekly.',
  },
];

function VideoCard({ videoId, label }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div
      onClick={!playing ? () => setPlaying(true) : undefined}
      style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', borderRadius: '14px', overflow: 'hidden', background: '#000', cursor: playing ? 'default' : 'pointer' }}
    >
      {playing ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
          allow="autoplay; encrypted-media"
          allowFullScreen
          title={label}
        />
      ) : (
        <>
          <img
            src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
            alt={label}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.25)' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #ff2d78, #ff6b35)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 32px rgba(255,45,120,.4)' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z" /></svg>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function LandingPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [showCheckout, setShowCheckout] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(false);

  const checkoutRef = useRef(null);
  const pricingRef = useRef(null);

  useEffect(() => {
    if (showCheckout && clientSecret && checkoutRef.current) {
      checkoutRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showCheckout, clientSecret]);

  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://us-west4-smartincomeig-eb2af.cloudfunctions.net/createcheckoutsession', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: PRICE_ID,
          successUrl: `${window.location.origin}/thank-you?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/`,
        }),
      });
      const { clientSecret } = await response.json();
      setShowCheckout(true);
      setClientSecret(clientSecret);
    } catch (err) {
      console.error('Checkout failed:', err);
      alert('Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="noise" />

      <nav className="nav">
        <span className="nav-logo">@AskSophiaLee</span>
        <button className="nav-cta" onClick={scrollToPricing}>Get it — $20</button>
      </nav>

      <section className="hero">
        <span className="hero-eyebrow">The DM Closer Framework</span>
        <h1 className="hero-headline">
          ManyChat fills your inbox.<br />
          <span className="gradient-text">The framework cashes it out.</span>
        </h1>
        <p className="hero-sub">
          The conversion framework operators use to turn inbox volume into booked calls, paid applications, and direct sales. Drop it into Claude or ChatGPT. Prompt. Close. Success.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <button className="btn-primary" onClick={scrollToPricing}>
            Get the framework — $20 →
          </button>
        </div>
      </section>

      <div className="proof-strip">
        {[
          'Closed three discovery calls in the first week I ran it',
          'My ghost rate dropped below 40%. Used to be 80',
          'Stopped writing DMs from scratch. The framework writes them now.',
        ].map((t, i) => (
          <span key={i} className="proof-pill">{t}</span>
        ))}
      </div>

      <section className="section">
        <div className="section-label">The full stack</div>
        <h2 className="section-title">ManyChat opens the thread.<br />We close it.</h2>
        <p className="section-sub">ManyChat automates the initial trigger and template delivery. The moment a human setter intercepts the thread, they execute Beat 1 (Acknowledge) to humanize the loop, bypass the generic bot feel, and seamlessly reset the buyer conversation format.</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', margin: '40px 0 24px' }}>
          <div style={{ background: 'var(--card-bg)', border: '1.5px solid var(--card-border)', borderRadius: 'var(--radius)', padding: '24px' }}>
            <p style={{ margin: '0 0 6px', fontSize: '.72rem', fontWeight: 800, letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--ink2)' }}>Traffic Entry Points</p>
            <p style={{ margin: '0 0 8px', fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--ink)', lineHeight: 1 }}>The Signal</p>
            <p style={{ margin: 0, fontSize: '.85rem', color: 'var(--ink2)', lineHeight: 1.5 }}>ManyChat automations, organic inbound DMs, LinkedIn connection responses, or story interactions.</p>
          </div>
          <div style={{ background: 'linear-gradient(135deg, rgba(191,90,242,.08), rgba(255,45,120,.08))', border: '1.5px solid rgba(255,45,120,.25)', borderRadius: 'var(--radius)', padding: '24px' }}>
            <p style={{ margin: '0 0 6px', fontSize: '.72rem', fontWeight: 800, letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--pink)' }}>DM Closer Framework</p>
            <p style={{ margin: '0 0 8px', fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--ink)', lineHeight: 1 }}>Beats 1 · 2 · 3 · 4</p>
            <p style={{ margin: 0, fontSize: '.85rem', color: 'var(--ink2)', lineHeight: 1.5 }}>Acknowledge, Diagnose, Permission, Pivot. The standalone prompt layout that scripts every reply until they close.</p>
          </div>
        </div>

        <p style={{ fontSize: '.88rem', color: 'var(--ink2)', lineHeight: 1.6, maxWidth: '560px', borderLeft: '3px solid var(--gold)', paddingLeft: '14px', margin: 0 }}>
          Whether a warm lead is delivered by ManyChat or drops a high-intent organic message directly into your inbox, winging the conversation is where conversions die. This framework acts as a universal sales system the second a human takes over the keyboard.
        </p>
      </section>

      <section className="section" id="beats">
        <div className="section-label">The mechanism</div>
        <h2 className="section-title">Four beats. In order. Always.</h2>
        <p className="section-sub">Every closing thread runs the same sequence. Miss a beat and the buyer drifts. Hit all four and they ask for the link. The framework names each beat, gives you the script architecture, and refuses to let you skip ahead.</p>

        <div className="product-grid">
          {BEATS.map((b) => (
            <div className="product-card" key={b.id}>
              <span className="product-emoji">{b.emoji}</span>
              <h3 className="product-card-title">{b.title}</h3>
              <p className="product-tagline">{b.tagline}</p>
              <ul className="product-deliverables">
                {b.deliverables.map((d, i) => (
                  <li key={i}><span>✓</span>{d}</li>
                ))}
              </ul>
              <p className="product-promise">"{b.promise}"</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-dark text-left">
        <div className="section-label">Prompt System Architecture</div>
        <h2 className="section-title">Built like raw software.<br />Engineered for LLMs.</h2>
        <p className="section-sub">Shallow prompts spit out robotic answers. This playbook structure forces Claude and ChatGPT to process multi-variable client states using deep systemic constraints.</p>

        <div className="code-window" style={{ background: '#0d1117', padding: '24px', borderRadius: '12px', fontFamily: 'monospace', fontSize: '.85rem', color: '#c9d1d9', border: '1px solid #30363d', textAlign: 'left', overflowX: 'auto', margin: '30px auto', maxWidth: '640px' }}>
          <span style={{ color: '#ff7b72' }}>&lt;seller_context&gt;</span><br />
          &nbsp;&nbsp;channel: <span style={{ color: '#a5d6ff' }}>Instagram | LinkedIn</span><br />
          &nbsp;&nbsp;current_offer: <span style={{ color: '#a5d6ff' }}>{"{name + price + delivery}"}</span><br />
          &nbsp;&nbsp;capacity: <span style={{ color: '#a5d6ff' }}>{"{monthly onboarding limit}"}</span><br />
          <span style={{ color: '#ff7b72' }}>&lt;/seller_context&gt;</span><br />
          <span style={{ color: '#ff7b72' }}>&lt;dm_thread_context&gt;</span><br />
          &nbsp;&nbsp;trigger: <span style={{ color: '#a5d6ff' }}>inbound | comment | story_reply | lead_magnet</span><br />
          &nbsp;&nbsp;buyer_signal: <span style={{ color: '#a5d6ff' }}>{"{exact copy-pasted user text}"}</span><br />
          &nbsp;&nbsp;automation_state: <span style={{ color: '#a5d6ff' }}>waiting_on_reply | stalled_in_flow</span><br />
          <span style={{ color: '#ff7b72' }}>&lt;/dm_thread_context&gt;</span>
        </div>
      </section>

      <section className="section section-dark">
        <div className="section-label">How it runs</div>
        <h2 className="section-title">Three steps. That's the whole setup.</h2>

        <div className="steps">
          {[
            { n: '01', title: 'Download the framework', desc: 'One file. Markdown. Lives in your Claude or ChatGPT project so every DM you write inherits it automatically.' },
            { n: '02', title: 'Add your business context', desc: 'Paste your offer, price, ICP, and voice into the top of the file. The framework does the rest.' },
            { n: '03', title: 'Prompt and close', desc: 'Open the project. Paste the DM that just landed. Ask for the next message. Send. Watch the close run.' },
          ].map((s, i) => (
            <div className="step" key={i}>
              <span className="step-num">{s.n}</span>
              <div>
                <h4 className="step-title">{s.title}</h4>
                <p className="step-desc">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="section-label">See it in action</div>
        <h2 className="section-title">Live in 2 minutes.</h2>
        <p className="section-sub">Drop the framework into a Claude project once. Every DM thread you open from that point forward inherits it automatically.</p>

        <VideoCard videoId="GJ5jTgcbRHA" label="How to start a project in Claude" />

        <div style={{ marginTop: '20px', padding: '20px 24px', borderRadius: '12px', borderLeft: '3px solid var(--gold)', background: 'rgba(255,209,102,.06)' }}>
          <p style={{ margin: 0, fontSize: '.9rem', color: 'var(--ink2)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--ink)' }}>Using ChatGPT?</strong> Same move — upload the file to a GPT Project. Identical setup, identical result.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="section-label">What's inside</div>
        <h2 className="section-title">The modules doing the heavy lifting.</h2>
        <p className="section-sub">Four hidden systems carry the framework past the opener and all the way to the close.</p>

        <div style={{ display: 'grid', gap: '12px', margin: '36px 0 0' }}>
          {VALUE_STACK.map((row, i) => (
            <div
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(160px, 0.9fr) 1.6fr',
                gap: '20px',
                alignItems: 'start',
                background: 'var(--card-bg)',
                border: '1.5px solid var(--card-border)',
                borderRadius: 'var(--radius)',
                padding: '20px 24px',
              }}
            >
              <p style={{ margin: 0, fontWeight: 800, fontSize: '.95rem', color: 'var(--ink)', lineHeight: 1.3 }}>{row.module}</p>
              <p style={{ margin: 0, fontSize: '.88rem', color: 'var(--ink2)', lineHeight: 1.6 }}>{row.value}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section pricing-section" id="pricing" ref={pricingRef}>
        <div className="section-label">Get the framework</div>
        <h2 className="section-title">$20. One time.</h2>
        <p className="section-sub">Stop burning $3,000 and $5,000 deals sitting in your unoptimized ManyChat logs. One single Markdown file deployment instantly shifts your Claude and ChatGPT models into an elite human closer. $20 one time. Yours forever.</p>

        <div className="checkout-wrapper">
          <div className="order-summary">
            <div className="order-line">
              <span>DM Closer Framework</span>
              <span className="order-price">$20</span>
            </div>
            <div className="order-total">
              <span>Total</span>
              <span>$20</span>
            </div>

            <div className="risk-stack">
              <div className="risk-row">
                <span className="risk-num">01</span>
                <div>
                  <strong>Instant download.</strong>
                  <p>One Markdown file. Drop it into your AI project the moment you pay. Running in under 5 minutes.</p>
                </div>
              </div>
              <div className="risk-row">
                <span className="risk-num">02</span>
                <div>
                  <strong>One price. No subscription.</strong>
                  <p>$20 once. Yours forever. Run it on every DM thread you'll ever open.</p>
                </div>
              </div>
            </div>

            <button
              ref={checkoutRef}
              className="checkout-btn"
              onClick={handleCheckout}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get the framework — $20 →'}
            </button>
            <p className="checkout-fine">Stripe handles payment securely. Instant download. No subscriptions.</p>
          </div>

          {showCheckout && clientSecret && (
            <div className="embedded-checkout">
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
        </div>
      </section>

      <section className="section finale">
        <h2 className="finale-title">
          Every DM has 4 beats.<br />
          <span className="gradient-text">Stop skipping them.</span>
        </h2>
        <p className="finale-sub">$20. One file. Every close you'll ever run.</p>
        <button className="btn-primary btn-xl" onClick={scrollToPricing}>Yes, send me the framework</button>
      </section>

      <footer className="footer">
        <span>© 2026 @AskSophiaLee</span>
        <span>Results depend on volume, offer clarity, and follow-through.</span>
      </footer>

      <div className="sticky-cta">
        <button onClick={scrollToPricing}>Get the framework — $20 →</button>
      </div>
    </div>
  );
}

export default LandingPage;